import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ImagePlus, Mail, MapPin, Phone, Send, X } from "lucide-react";
import { WHATSAPP_URL, WhatsAppIcon } from "./WhatsAppButton";
import { CONTACT } from "@/data/content";
import { SectionHeading } from "./PriceMenu";

const BASE = process.env.REACT_APP_BACKEND_URL;
const API = `${BASE}/api`;
const MAX_IMAGES = 4;

const EVENT_TYPES = [
  "Birthday Cake",
  "Kids Cake",
  "Theme Cake",
  "Anniversary Cake",
  "Bento Cake & Cupcakes Box",
  "Cupcakes / Cookies / Brownies",
  "Other",
];
const BUDGETS = ["Under €100", "€100 – €200", "€200 – €400", "€400+"];

const EMPTY = {
  name: "",
  email: "",
  phone: "",
  event_type: "Birthday Cake",
  event_date: "",
  guest_count: "",
  budget_tier: "",
  message: "",
};

const inputCls =
  "w-full rounded-sm border border-[#D4AF37]/25 bg-[#FDFBF7]/70 px-4 py-3 text-sm text-[#1B3A6B] placeholder-[#67718A]/70 outline-none transition-colors duration-300 focus:border-[#D4AF37]";

export const EnquiryForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);

  const uploadImages = async (files) => {
    const list = Array.from(files || []).slice(0, MAX_IMAGES - images.length);
    if (!list.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const file of list) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await axios.post(`${API}/enquiries/upload`, fd);
        urls.push(`${BASE}/api/files/${data.path}`);
      }
      setImages((prev) => [...prev, ...urls]);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          "Could not upload image. Please use JPG or PNG under 5 MB.",
      );
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    const handler = (e) => {
      const { event_type, message } = e.detail || {};
      setForm((f) => ({
        ...f,
        ...(event_type ? { event_type } : {}),
        ...(message
          ? { message: f.message ? `${f.message} ${message}` : message }
          : {}),
      }));
    };
    window.addEventListener("prefill-enquiry", handler);
    return () => window.removeEventListener("prefill-enquiry", handler);
  }, []);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please add your name and email so we can reply.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, {
        ...form,
        guest_count: form.guest_count ? Number(form.guest_count) : null,
        reference_images: images,
      });
      toast.success("Enquiry received — Tarah will reply within 24 hours.");
      setForm(EMPTY);
      setImages([]);
    } catch {
      toast.error(
        "Something went wrong sending your enquiry. Please try again.",
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="enquiry"
      data-testid="enquiry-section"
      className="border-t border-[#D4AF37]/10 bg-[#F7F2E8]/60 py-24 lg:py-32"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <SectionHeading
          eyebrow="Get in Touch"
          title="Order Enquiry"
          testId="enquiry-heading"
        />

        <div className="mt-14 grid gap-14 lg:grid-cols-5">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-2"
          >
            <p className="font-display text-2xl italic leading-snug text-[#8A6D1F]">
              Every Tarah cake begins with a conversation.
            </p>
            <p className="mt-5 text-sm leading-relaxed text-[#67718A]">
              Tell us about your celebration — the date, the guest list, the
              mood you're dreaming of. We'll reply within 24 hours with
              availability, a design direction and a tailored quote.
            </p>
            <div className="mt-10 space-y-5 text-sm text-[#2F4A78]">
              <p
                className="flex items-center gap-3"
                data-testid="contact-email"
              >
                <Mail size={16} className="text-[#D4AF37]" /> {CONTACT.email}
              </p>
              <p
                className="flex items-center gap-3"
                data-testid="contact-phone"
              >
                <Phone size={16} className="text-[#D4AF37]" /> {CONTACT.phone}
              </p>
              <a
                data-testid="contact-whatsapp-link"
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 transition-colors hover:text-[#25D366]"
              >
                <WhatsAppIcon size={16} className="text-[#25D366]" /> Message us on WhatsApp
              </a>
              <p
                className="flex items-center gap-3"
                data-testid="contact-address"
              >
                <MapPin size={16} className="text-[#D4AF37]" />{" "}
                {CONTACT.address}
              </p>
              <p className="pt-2 text-xs uppercase tracking-[0.22em] text-[#67718A]">
                {CONTACT.hours}
              </p>
            </div>
          </motion.div>

          <motion.form
            onSubmit={submit}
            data-testid="enquiry-form"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="rounded-sm border border-[#D4AF37]/25 bg-[#FFFFFF]/70 p-8 backdrop-blur-xl lg:col-span-3 lg:p-10"
          >
            <div className="grid gap-5 sm:grid-cols-2">
              <input
                data-testid="enquiry-name-input"
                className={inputCls}
                placeholder="Your name *"
                value={form.name}
                onChange={set("name")}
              />
              <input
                data-testid="enquiry-email-input"
                type="email"
                className={inputCls}
                placeholder="Email address *"
                value={form.email}
                onChange={set("email")}
              />
              <input
                data-testid="enquiry-phone-input"
                className={inputCls}
                placeholder="Phone *"
                value={form.phone}
                onChange={set("phone")}
              />
              <select
                data-testid="enquiry-event-type-select"
                className={inputCls}
                value={form.event_type}
                onChange={set("event_type")}
              >
                {EVENT_TYPES.map((t) => (
                  <option key={t} value={t} className="bg-[#FDFBF7]">
                    {t}
                  </option>
                ))}
              </select>
              <input
                data-testid="enquiry-date-input"
                type="date"
                className={inputCls}
                value={form.event_date}
                onChange={set("event_date")}
              />
              <input
                data-testid="enquiry-guest-count-input"
                type="number"
                min="1"
                className={inputCls}
                placeholder="Guest count"
                value={form.guest_count}
                onChange={set("guest_count")}
              />
              <select
                data-testid="enquiry-budget-select"
                className={`${inputCls} sm:col-span-2`}
                value={form.budget_tier}
                onChange={set("budget_tier")}
              >
                <option value="" className="bg-[#FDFBF7]">
                  Budget range (optional)
                </option>
                {BUDGETS.map((b) => (
                  <option key={b} value={b} className="bg-[#FDFBF7]">
                    {b}
                  </option>
                ))}
              </select>
              <textarea
                data-testid="enquiry-message-input"
                rows={5}
                className={`${inputCls} resize-none sm:col-span-2`}
                placeholder="Tell us about your celebration — theme, colours, flavours, inspiration…"
                value={form.message}
                onChange={set("message")}
              />
            </div>

            <div
              className="mt-4 rounded-sm border border-dashed border-[#D4AF37]/40 bg-[#FBF7EE]/50 p-4"
              data-testid="enquiry-images-section"
            >
              <p className="text-xs uppercase tracking-[0.2em] text-[#9A7B24]">
                Reference images
              </p>
              <p className="mt-1 text-xs text-[#67718A]">
                Add up to {MAX_IMAGES} inspiration photos — a cake you love,
                colours or a theme (optional, JPG/PNG under 5 MB each).
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                {images.map((url, i) => (
                  <div
                    key={url}
                    className="relative"
                    data-testid={`enquiry-image-preview-${i}`}
                  >
                    <img
                      src={url}
                      alt={`Reference ${i + 1}`}
                      className="h-20 w-24 rounded-sm object-cover"
                    />
                    <button
                      type="button"
                      data-testid={`enquiry-image-remove-${i}`}
                      onClick={() =>
                        setImages((prev) => prev.filter((u) => u !== url))
                      }
                      aria-label="Remove image"
                      className="absolute -right-2 -top-2 rounded-full bg-[#1B3A6B] p-1 text-white transition-colors hover:bg-[#8A6D1F]"
                    >
                      <X size={11} />
                    </button>
                  </div>
                ))}
                {images.length < MAX_IMAGES && (
                  <label
                    data-testid="enquiry-image-upload"
                    className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-[#D4AF37]/50 px-4 py-2 text-xs font-medium text-[#8A6D1F] transition-colors hover:bg-[#D4AF37] hover:text-[#1B3A6B]"
                  >
                    <ImagePlus size={14} />{" "}
                    {uploading ? "Uploading…" : "Add images"}
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      disabled={uploading}
                      onChange={(e) => uploadImages(e.target.files)}
                    />
                  </label>
                )}
              </div>
            </div>

            <button
              data-testid="enquiry-form-submit-button"
              type="submit"
              disabled={sending || uploading}
              className="mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-[#D4AF37] px-8 py-4 text-sm font-semibold tracking-wide text-[#1B3A6B] transition-all duration-300 hover:bg-[#8A6D1F] disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
            >
              {sending ? "Sending…" : "Send Enquiry"}
              <Send size={15} />
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
};
